import { useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePatronsSearch } from "@/api/hooks";
import type { Patron, PatronStatus } from "@/api/types";

const STATUS_LABELS: Record<PatronStatus, string> = {
  active: "Actif",
  blocked: "Bloqué",
  expired: "Expiré",
  suspended: "Suspendu",
};

const STATUS_COLORS: Record<PatronStatus, string> = {
  active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  blocked: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  expired: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  suspended: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
};

export function PatronsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(searchParams.get("q") ?? "");

  const query = searchParams.get("q") ?? "";
  const statusFilter = searchParams.get("status") as PatronStatus | null;

  const {
    data: patrons,
    isLoading,
    error,
  } = usePatronsSearch(query || undefined, statusFilter || undefined);

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const params = new URLSearchParams(searchParams);
      if (inputValue.trim()) {
        params.set("q", inputValue.trim());
      } else {
        params.delete("q");
      }
      setSearchParams(params);
    },
    [inputValue, searchParams, setSearchParams]
  );

  const handleStatusFilter = useCallback(
    (status: PatronStatus | null) => {
      const params = new URLSearchParams(searchParams);
      if (status) {
        params.set("status", status);
      } else {
        params.delete("status");
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Lecteurs</h1>
        <Link
          to="/circulation/patrons/new"
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
        >
          <span>+</span>
          Nouveau lecteur
        </Link>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1">
          <input
            type="search"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
            }}
            placeholder="Rechercher par nom, code-barres, email..."
            className="w-full rounded-lg border border-gray-300 px-4 py-3 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <svg
            className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          type="submit"
          className="rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          Rechercher
        </button>
      </form>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => {
            handleStatusFilter(null);
          }}
          className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
            !statusFilter
              ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Tous
        </button>
        {(["active", "blocked", "expired", "suspended"] as PatronStatus[]).map((status) => (
          <button
            key={status}
            onClick={() => {
              handleStatusFilter(status);
            }}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              statusFilter === status
                ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {STATUS_LABELS[status]}
          </button>
        ))}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          Une erreur est survenue lors de la recherche.
        </div>
      ) : patrons && patrons.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {patrons.length} lecteur{patrons.length > 1 ? "s" : ""}
          </p>
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {patrons.map((patron) => (
              <PatronRow key={patron.id} patron={patron} />
            ))}
          </div>
        </div>
      ) : query || statusFilter ? (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Aucun lecteur trouvé</p>
        </div>
      ) : (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            Entrez un terme de recherche pour commencer
          </p>
        </div>
      )}
    </div>
  );
}

function PatronRow({ patron }: { patron: Patron }) {
  return (
    <Link
      to={`/circulation/patrons/${patron.id}`}
      className="block p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h3 className="font-medium text-gray-900 dark:text-white">{patron.displayName}</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[patron.status]}`}
            >
              {STATUS_LABELS[patron.status]}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span>{patron.barcode}</span>
            {patron.email && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>{patron.email}</span>
              </>
            )}
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <span>{patron.category.name}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm">
          {patron.activeLoanCount > 0 && (
            <span className="text-gray-500 dark:text-gray-400">
              {patron.activeLoanCount} prêt{patron.activeLoanCount > 1 ? "s" : ""}
            </span>
          )}
          {patron.fineBalanceInCents > 0 && (
            <span className="font-medium text-orange-600 dark:text-orange-400">
              {(patron.fineBalanceInCents / 100).toFixed(2)} €
            </span>
          )}
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </Link>
  );
}

export default PatronsPage;
